import { toastError, toastSuccess } from '@/utils/toast'
import { createSdk } from '@knots/api-sdk'
import { createSdk as createStudioSdk } from '@knots/studio-sdk'
import { type Middleware } from 'openapi-fetch'
import { API_URL, CF_API_URL, CODE_APP_API_URL } from '../config'
import tokenStorage from '../features/auth/stores/token.storage'

const auth: Middleware = {
  async onRequest({ request }) {
    const token = tokenStorage.getToken()

    if (token) {
      request.headers.set('Authorization', `Bearer ${token}`)
    }

    return request
  },
}

// A 401/403 here means the login attempt failed, not that the session expired
const authExemptPaths = ['/oauth2/token', '/sso/login']

const authFailHandler: Middleware = {
  onResponse({ response }) {
    const urlObj = new URL(response.url)
    const isAuthExempt = authExemptPaths.includes(urlObj.pathname)

    try {
      if (!isAuthExempt && (response.status === 403 || response.status === 401)) {
        tokenStorage.clearToken()
        window.location.assign(window.location.origin as unknown as string)
      }

      return response
    } catch (error: any) {
      if (!isAuthExempt && (error.status === 403 || error.status === 401)) {
        tokenStorage.clearToken()
        window.location.assign(window.location.origin as unknown as string)
        throw new Error('Bad authentication token')
      } else {
        throw error
      }
    }
  },
}

const serverSuccessHandler: Middleware = {
  async onResponse({ response, request }) {
    const data = await response.clone().json()

    if (response.status >= 200 && response.status < 300) {
      if (data?.message) {
        toastSuccess(`${data.message}`)
      }
    }

    if (response.status >= 400 || data?.error) {
      const isSuccessDeleteRequest = request.method === 'DELETE' && response.status === 404
      if (isSuccessDeleteRequest) return response

      const errorMessage = getErrorMessage({ data })
      toastError('Error', errorMessage)
      throw errorMessage
    }

    return response
  },
}

const getErrorMessage = (object: any) => {
  const data =
    object?.errorMessage ??
    object?.data?.errorMessage ??
    object?.data?.intlMessage ??
    object?.data?.error ??
    object?.data?.message ??
    'Something gone wrong'

  if (data === 'invalid_grant' || data === 'invalid_client') {
    return 'Wrong email or password'
  }

  return data
}

const serverFailHandler: Middleware = {
  onError({ error }) {
    toastError('Error', getErrorMessage(error))
    throw error
  },
}

export const studioApiSdk = createStudioSdk({
  baseUrl: CODE_APP_API_URL,
  headers: {
    Accept: 'application/json',
  },
})

studioApiSdk.use({
  async onResponse({ response }) {
    if (!response.ok) {
      throw new Error(`${response.url}: ${response.status} ${response.statusText} ${await response.text()}`)
    }
  },
})
studioApiSdk.use(auth)
studioApiSdk.use(serverSuccessHandler)
studioApiSdk.use(serverFailHandler)
studioApiSdk.use(authFailHandler) // Should be last to properly handle auth errors

export const knotsApiSdk = createSdk({
  baseUrl: API_URL,
})

knotsApiSdk.use(auth)
knotsApiSdk.use(serverFailHandler)
knotsApiSdk.use(serverSuccessHandler)
knotsApiSdk.use(authFailHandler) // Should be last to properly handle auth errors

export const knotsCFApiSdk = createSdk({
  baseUrl: CF_API_URL,
})

knotsCFApiSdk.use(serverFailHandler)
knotsCFApiSdk.use(serverSuccessHandler)
